import {
  InvalidateQueryFilters,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { PageTemplateAPI } from 'api/page-template';
import {
  PageType,
  SourceType,
  PageTemplate,
  PageTemplateLite,
} from 'api/page-template/types';
import { useCallback } from 'react';

const getAllTemplatesKey = (pageType: PageType, sourceType: SourceType) => [
  'page-templates',
  pageType,
  sourceType,
];

type GetAllTemplatesQueryKeyType = ReturnType<typeof getAllTemplatesKey>;

type GetAllPageTemplateQueryOptions<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = Omit<
  UseQueryOptions<
    PageTemplateLite<PageTypeT, SourceTypeT>[],
    Error,
    PageTemplateLite<PageTypeT, SourceTypeT>[],
    GetAllTemplatesQueryKeyType
  >,
  'queryKey' | 'queryFn'
>;

const useGetAllPageTemplatesQuery = <
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
>(
  pageType: PageTypeT,
  sourceType: SourceTypeT,
  queryOptions?: GetAllPageTemplateQueryOptions<PageTypeT, SourceTypeT>,
): UseQueryResult<PageTemplateLite<PageTypeT, SourceTypeT>[], Error> =>
  useQuery({
    queryKey: getAllTemplatesKey(pageType, sourceType),
    queryFn: () => PageTemplateAPI.getAllPageTemplates(pageType, sourceType),
    ...queryOptions,
  });

const getPageTemplateQueryKey = (pageTemplateId: number) =>
  ['page-template', pageTemplateId] as const;

type PageTemplateQueryKeyType = ReturnType<typeof getPageTemplateQueryKey>;

type GetPageTemplateQueryOptions<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = Omit<
  UseQueryOptions<
    PageTemplate<PageTypeT, SourceTypeT>,
    Error,
    PageTemplate<PageTypeT, SourceTypeT>,
    PageTemplateQueryKeyType
  >,
  'queryKey' | 'queryFn'
>;

const useGetPageTemplateQuery = <
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
>(
  pageTemplateId: number,
  queryOptions?: GetPageTemplateQueryOptions<PageTypeT, SourceTypeT>,
): UseQueryResult<PageTemplate<PageTypeT, SourceTypeT>, Error> =>
  useQuery({
    queryKey: getPageTemplateQueryKey(pageTemplateId),
    queryFn: () =>
      PageTemplateAPI.getPageTemplate<PageTypeT, SourceTypeT>(pageTemplateId),
    ...queryOptions,
  });

const useInvalidateAllPageTemplatesQuery = (
  pageType: PageType,
  sourceType: SourceType,
) => {
  const queryClient = useQueryClient();

  return useCallback(
    async (options?: InvalidateQueryFilters) => {
      await queryClient.invalidateQueries({
        queryKey: getAllTemplatesKey(pageType, sourceType),
        ...options,
      });
    },
    [queryClient, pageType, sourceType],
  );
};

const useInvalidatePageTemplateQuery = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (pageTemplateId: number) => {
      await queryClient.invalidateQueries({
        queryKey: getPageTemplateQueryKey(pageTemplateId),
      });
    },
    [queryClient],
  );
};

interface PageTemplatePreviewParams {
  meetingId: number;
  pageType: PageType;
  enabled?: boolean;
  payload?: object;
}

const useGetPageTemplatePreviewQuery = ({
  meetingId,
  pageType,
  enabled = false,
  payload,
}: PageTemplatePreviewParams) => {
  return useQuery({
    queryKey: ['page-template-preview', meetingId, pageType],
    queryFn: ({ signal }) =>
      PageTemplateAPI.getPageLevelTemplatePreview(
        meetingId,
        pageType,
        payload,
        signal,
      ),
    enabled,
    retry: 3,
    gcTime: 0,
    staleTime: 0,
  });
};

interface SectionPreviewParams {
  meetingId: number;
  pageType: PageType;
  payload: object;
  enabled?: boolean;
  streaming?: boolean;
}
export const useGetSectionPreviewQuery = ({
  meetingId,
  pageType,
  payload,
  streaming = true,
  enabled = false,
}: SectionPreviewParams) => {
  return useQuery({
    queryKey: ['section-preview', meetingId, pageType],
    queryFn: () =>
      PageTemplateAPI.getSectionLevelPreview(
        meetingId,
        pageType,
        payload,
        streaming,
      ),
    enabled,
    retry: 3,
    gcTime: 0,
    staleTime: 0,
  });
};

export {
  getAllTemplatesKey,
  type GetAllPageTemplateQueryOptions as GetAllPageTemplateQueryOption,
  useGetAllPageTemplatesQuery,
  getPageTemplateQueryKey,
  type GetPageTemplateQueryOptions as GetPageTemplateQueryOption,
  useGetPageTemplateQuery,
  useInvalidateAllPageTemplatesQuery,
  useInvalidatePageTemplateQuery,
  useGetPageTemplatePreviewQuery,
};
